import React from 'react';
import RichText from '@components/rich-text/rich-text';
import vars from '@variables';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { H2Atom } from '@atoms/text-atoms/text-atoms';
import { ImageBlockProps } from './image-block.types';
import { ImageBlockContainer } from './image-block.styles';
import useWindowSize from '@hooks/use-window-size';

const ImageBlock: React.FC<ImageBlockProps> = ({
  anchorId,
  title = '',
  images = [],
  mobileImages = [],
  colour = vars.coreDarkGrey,
  className,
  narrowWidth = false,
  firstImageLink,
  firstImageOpenInNewTab = false,
  secondImageLink,
  secondImageOpenInNewTab = false,
}) => {
  const maxImages = 2;
  const screenWidth = useWindowSize().width;
  if (!images || images.length < 1) return null;

  const isMobile = !!screenWidth && screenWidth <= 768;
  const useMobileImages = isMobile && mobileImages?.length > 0;
  const imagesToRender = useMobileImages ? mobileImages : images;

  const renderImage = (image: any, index: number) => {
    const gatsbyImage = getImage(image.gatsbyImageData);
    const imageLink = index === 0 ? firstImageLink : secondImageLink;
    const openInNewTab =
      index === 0 ? firstImageOpenInNewTab : secondImageOpenInNewTab;

    const imageContent = (
      <>
        {gatsbyImage &&
        image.format !== 'image/gif' &&
        image.format !== 'image/svg+xml' ? (
          <GatsbyImage
            image={gatsbyImage}
            alt={image.alt ? image.alt : ''}
            className="gatsby-image-pos"
          />
        ) : (
          <div>{image.src && <img src={image.src} alt={image.alt} />}</div>
        )}

        {image.description && <RichText content={image.description} />}
      </>
    );

    return (
      <div
        className="image-with-caption-wrapper"
        data-testid={`image-${index}`}
        key={`image-${index}`}
        tabIndex={index}
      >
        {imageLink ? (
          <a
            href={imageLink}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
          >
            {imageContent}
          </a>
        ) : (
          imageContent
        )}
      </div>
    );
  };

  return (
    <ImageBlockContainer
      id={anchorId}
      className={className}
      colour={colour}
      images={useMobileImages ? mobileImages : images}
      narrowWidth={narrowWidth}
    >
      {title && <H2Atom>{title}</H2Atom>}
      <div className="content-container">
        {imagesToRender
          .slice(0, maxImages)
          .map((image, index) => renderImage(image, index))}
      </div>
    </ImageBlockContainer>
  );
};

export default ImageBlock;
